// @file Composable to manage surface navbar visibility state, specifically for AI chat visibility
import { captureException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { getSafeStorage } from '@@/bits/safe_storage'
import { isRegistered } from '@@/bits/user_model'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceAccountsStore } from '@@/pinia/surface_accounts_store'
import { useSurfaceAIChatStore } from '@@/pinia/surface_ai_chat_store'
import { useCommentsStore } from '@@/pinia/surface_comments'
import { useSurfaceGuestStore } from '@@/pinia/surface_guest_store'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceRemakeLinkStore } from '@@/pinia/surface_remake_link_store'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { useWindowSizeStore } from '@@/pinia/window_size'
import { createSharedComposable, tryOnMounted } from '@vueuse/core'
import { computed, ref, watch } from 'vue'

/**
 * ID for the avatar button element used as anchor for guest ID card or user menu popover.
 * This should be assigned to the avatar button in both SurfaceTopBar and SurfaceActionBar.
 */
export const AVATAR_EL_ID = 'guest-id-card-avatar-anchor'

const USER_CLOSED_APPROVE_ALL_BUTTON_MAP_KEY = 'userClosedApproveAllButtonMap'

export const useSurfaceNavbarVisibility = createSharedComposable(() => {
  const surfaceStore = useSurfaceStore()
  const surfaceAccountsStore = useSurfaceAccountsStore()
  const surfaceAIChatStore = useSurfaceAIChatStore()
  const surfacePermissionsStore = useSurfacePermissionsStore()
  const surfaceTranslationsStore = useSurfaceTranslationsStore()
  const demoPadletPanelStore = useSurfaceOnboardingDemoPadletPanelStore()
  const surfaceGuestStore = useSurfaceGuestStore()
  const windowSizeStore = useWindowSizeStore()
  const surfacePostsStore = useSurfacePostsStore()
  const commentsStore = useCommentsStore()
  const surfaceRemakeLinkStore = useSurfaceRemakeLinkStore()

  // #region Nav bar mounted state
  /**
   * Used for:
   * - displaying the add post button, and then teleporting the add post button to the nav bar.
   */
  const isActionBarMounted = ref(false)
  function setIsActionBarMounted(newValue: boolean): void {
    isActionBarMounted.value = newValue
  }
  // #endregion

  const xAiChat = computed(() => {
    if (!surfacePermissionsStore.amIOwner) return false
    if (!demoPadletPanelStore.demoPadletPanelVisibility.showSurfaceAiChat) return false
    if (surfaceTranslationsStore.isViewingTranslatedPadlet) return false

    // For users that specifically enabled surface chat or are native admins, we show it
    if (isAppUsing('surfaceAiChat') || surfaceAccountsStore.isNativeAdmin) {
      return true
    }

    if (!surfaceStore.isMagicWall) return false
    if (surfaceStore.magicWallType === 'learning_assessment_polls') return false

    // Else, we show it for library and backpack teachers.
    if (surfaceAccountsStore.isLibraryTeacherOrAbove || surfaceAccountsStore.isBackpackTeacherOrAbove) return true

    return false
  })

  const xSignup = computed(() => {
    return isAppUsing('signUpSurface') && !isRegistered(surfaceStore.user) && !surfaceStore.isSimplifiedView
  })

  const xGuestIdCard = computed(() => {
    return (
      surfaceGuestStore.shouldEnableAnonymousAttribution &&
      !surfacePermissionsStore.amIRegistered &&
      surfaceGuestStore.xGuestIdCard &&
      !windowSizeStore.isSmallerThanTabletPortrait &&
      !surfaceGuestStore.isGuestIdLabelExpanded
    )
  })

  const xActivityPanelButton = computed<boolean>(() => {
    const wallBuilderId = surfaceStore.wallAttributes?.builder?.id
    if (wallBuilderId === null) return false

    // Check if there are posts from users other than the wall builder
    const hasPostsFromOthers = surfacePostsStore.publishedPosts.some((post) => post.author_id !== wallBuilderId)
    if (hasPostsFromOthers) return true

    // Check if there are comments from users other than the wall builder
    const hasCommentsFromOthers = Object.values(commentsStore.commentEntities).some(
      (comment) => comment.user_id !== wallBuilderId,
    )
    if (hasCommentsFromOthers) return true

    // Check if there are walls that have been remade from this wall
    if (surfaceRemakeLinkStore.hasWallsRemade) return true

    return false
  })

  // #region Translation
  const xTranslationUi = computed(() => !surfaceStore.isWhiteboard)
  const isViewingTranslatedPadlet = computed(() => surfaceTranslationsStore.isViewingTranslatedPadlet)
  const xTranslationBanner = computed(
    () =>
      xTranslationUi.value &&
      surfaceTranslationsStore.xTranslateToUserLocaleBanner &&
      surfaceTranslationsStore.isBannerExpanded,
  )
  const xOpenTranslationPanelButton = computed(() => {
    if (!xTranslationUi.value) return false

    // If user has dismissed the banner, translating from panel, or viewing translated padlet,
    // always show the globe button (they've already interacted with translation)
    if (
      surfaceTranslationsStore.hasDismissedTranslationBanner ||
      surfaceTranslationsStore.isViewingTranslatedPadlet ||
      surfaceTranslationsStore.isTranslatingPadletFromPanel
    ) {
      return true
    }

    // If currently translating from banner, only hide the globe button if banner is still visible
    // (i.e., banner hasn't been collapsed/dismissed yet, meaning this is the initial translation)
    if (surfaceTranslationsStore.isTranslatingPadletFromBanner) {
      return !surfaceTranslationsStore.isBannerExpanded
    }

    return false
  })
  // #endregion

  // #region Approval
  const isInCloseApproveAllButtonMap = (hashid: string) => {
    try {
      const mapString = getSafeStorage().getItem(USER_CLOSED_APPROVE_ALL_BUTTON_MAP_KEY) ?? '{}'
      const parsedMap = JSON.parse(mapString)
      return Boolean(parsedMap[hashid])
    } catch (err) {
      captureException(err, { tags: { source: 'SurfaceNavbarVisibilityIsInCloseApproveAllButtonMap' } })
      // if there is issue parsing USER_CLOSED_APPROVE_ALL_BUTTON_MAP_KEY
      // just reset. worst case user just sees button again
      getSafeStorage().setItem(USER_CLOSED_APPROVE_ALL_BUTTON_MAP_KEY, '{}')
      return false
    }
  }

  const shouldCloseApproveAllBanner = ref(isInCloseApproveAllButtonMap(surfaceStore.wallHashid))
  const numUnapprovedContributions = computed(() => {
    return surfacePostsStore.unapprovedPosts.length + commentsStore.unapprovedComments.length
  })

  const xApprovalBanner = computed(
    () =>
      numUnapprovedContributions.value >= 3 &&
      surfacePermissionsStore.canIAdminister &&
      !shouldCloseApproveAllBanner.value &&
      !surfaceStore.isSearchBarInEditMode &&
      !surfaceStore.isFrozen &&
      !isViewingTranslatedPadlet.value,
  )

  watch(numUnapprovedContributions, (newVal) => {
    if (newVal < 3) {
      shouldCloseApproveAllBanner.value = true
    }
  })

  const updateUserClosedApproveAllButtonMap = (hashid: string, value: boolean) => {
    try {
      const mapString = getSafeStorage().getItem(USER_CLOSED_APPROVE_ALL_BUTTON_MAP_KEY) ?? '{}'
      const parsedMap = JSON.parse(mapString)
      if (value === true) {
        parsedMap[hashid] = value
      } else {
        delete parsedMap[hashid]
      }
      getSafeStorage().setItem(USER_CLOSED_APPROVE_ALL_BUTTON_MAP_KEY, asciiSafeStringify(parsedMap))
    } catch (err) {
      // if there is issue with updating the map
      // just reset. worst case user just sees button again
      getSafeStorage().setItem(USER_CLOSED_APPROVE_ALL_BUTTON_MAP_KEY, '{}')
      captureException(err, { tags: { source: 'SurfaceNavbarVisibilityUpdateUserClosedApproveAllButtonMap' } })
    }
  }
  watch([() => surfacePostsStore.remotePosts, () => commentsStore.remoteComments], () => {
    // we only need to handle remote contribution editing and adding to distinguish from
    // initial fetch for posts/comments since fetching for posts/comments wont reduce numUnapprovedContributions
    if (numUnapprovedContributions.value >= 3) {
      updateUserClosedApproveAllButtonMap(surfaceStore.wallHashid, false)
      shouldCloseApproveAllBanner.value = false
    }
  })

  const closeApprovalBanner = () => {
    shouldCloseApproveAllBanner.value = true
    updateUserClosedApproveAllButtonMap(surfaceStore.wallHashid, true)
  }
  // #endregion

  // #region Notification banners
  const xNotificationBannerList = computed(() => xTranslationBanner.value || xApprovalBanner.value)
  // #endregion

  const initializeAiChat = async () => {
    await surfaceAccountsStore.initialize()

    // Load data to show feedback button, remove wall options if first time
    await surfaceAIChatStore.loadMagicWallFirstOpen(xAiChat.value)
  }

  tryOnMounted(async () => {
    await initializeAiChat()
  })

  return {
    xAiChat,
    xSignup,
    xGuestIdCard,
    xActivityPanelButton,
    isActionBarMounted,
    setIsActionBarMounted,
    xTranslationBanner,
    xOpenTranslationPanelButton,
    isViewingTranslatedPadlet,
    xApprovalBanner,
    closeApprovalBanner,
    xNotificationBannerList,
  }
})
